<template>
  <div
    id="sidebar_section"
    :class="[
      'sidebar proxima_nova text_color transition-all duration-300 ease-in-out',
      isExpanded ? 'w-60' : 'w-14',
    ]"
  >
    <div class="flex flex-col w-full h-full justify-between">
      <div class="flex flex-col h-full overflow-y-scroll">
        <ExpandedSidebar
          v-show="isExpanded"
          :options="filteredSideBarOptions"
          :active-section="activeSection"
          @section-click="handleSectionClick"
          @event-click="handleEventClick"
        />

        <CollapsedSidebar
          v-show="!isExpanded"
          :options="filteredSideBarOptions"
          @event-click="handleEventClick"
        />
      </div>

      <div
        :class="[
          'flex w-full transition-all duration-300 ease-in-out mt-4 mb-2',
          isExpanded ? 'justify-end px-2' : 'justify-center',
        ]"
      >
        <div @click="toggleExpand" class="card_container cursor-pointer p-2">
          <ThemeImage
            :icon="
              isExpanded
                ? imageNames.CHEVRON_ARROW_LEFT_GREY
                : imageNames.EXPAND_GREY
            "
            alt="expand_icon"
            class="size-4"
          />
        </div>
      </div>
    </div>

    <SidebarWrapper ref="sidebarWrapperRef" />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useAuthStore } from "@/src/composables/useAuthStore.js";
import { fetchPermissions } from "@/src/utils/permissionUtils.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { permissionConstants } from "@/src/constants/permissionConstants.js";
import { getSideBarOptionsWithDynamicCRM } from "@/src/utils/sidebarUtils.js";
import { useDynamicCRMObjects } from "@/src/composables/useSidebarFeatures.js";
import { localStorageConstants } from "@/src/constants/localStorageConstants.js";
import {
  isDynamicCRMEnabled,
  isStandardObjectsV2Enabled,
} from "@/src/utils/dynamicFormUtils.js";
import {
  sidebarOptionPosition,
  sideBarOptions,
  featureFlags,
} from "@/src/constants/sidebarConstants.js";
import { DESKTOP_ONLY_APP_CODES } from "@/src/constants/appStoreConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import AppStoreService from "@/src/api/AppStoreService.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SidebarWrapper from "@/src/components/sidebar_components/SidebarWrapper.vue";
import ExpandedSidebar from "@/src/components/sidebar_components/ExpandedSidebar.vue";
import CollapsedSidebar from "@/src/components/sidebar_components/CollapsedSidebar.vue";

const authStore = useAuthStore();
const store = useStore();
const route = useRoute();
const isExpanded = ref(true);
const activeSection = ref(null);
const sidebarWrapperRef = ref(null);
const dynamicSidebarSections = ref([]);
const isUserAuthenticated = computed(() => authStore.isAuthenticated.value);
const isPublicRoute = computed(() => route.meta.public === true);

const {
  dynamicCRMSections,
  allowedStandardObjects,
  fetchAllowedObjects,
  fetchDynamicCRMListObjects,
} = useDynamicCRMObjects();

const sideBarOptionsArr = computed(() => {
  const assistantItem = {
    id: "assistant",
    text: "I Assistant",
    permission: permissionConstants.ALL,
    selected: imageNames.ASSISTANT_SELECTED,
    unselected: imageNames.ASSISTANT_UNSELECTED,
    route: "/i-assistant",
    alignment: sidebarOptionPosition.TOP,
  };

  const appStoreItem = {
    id: "app_store",
    text: "Add Apps",
    permission: permissionConstants.ALL,
    selected: imageNames.APP_STORE,
    unselected: imageNames.APP_STORE,
    route: "/market",
    alignment: sidebarOptionPosition.TOP,
  };

  const findFallbackSection = (section) => {
    const normalizedSectionText = section.text?.toLowerCase();
    const isInboundAgent =
      normalizedSectionText === "inbound agent" ||
      normalizedSectionText === "support";

    return sideBarOptions.find((opt) => {
      if (opt.id === section.id) return true;
      if (opt.text?.toLowerCase() === normalizedSectionText) return true;
      if (
        isInboundAgent &&
        (opt.id === "inbound_agent" ||
          opt.text?.toLowerCase() === "support" ||
          opt.text?.toLowerCase() === "inbound agent")
      ) {
        return true;
      }
      return false;
    });
  };

  const getIcon = (section, fallback, type) => {
    const icon = section[type];
    const fallbackIcon = fallback?.[type];
    const defaultIcon =
      type === "selected"
        ? imageNames.PROSPECT_SELECTED
        : imageNames.PROSPECT_UNSELECTED;

    return icon && icon.trim() !== "" ? icon : fallbackIcon || defaultIcon;
  };

  const processChildSection = (child, fallbackSection) => {
    const fallbackChild = fallbackSection?.sections?.find(
      (fs) =>
        fs.id === child.id ||
        fs.text?.toLowerCase() === child.text?.toLowerCase(),
    );

    const processedChild = {
      id: child.id,
      text: child.text,
      permission:
        child.permission ||
        fallbackChild?.permission ||
        permissionConstants.ALL,
      selected: child.selected || fallbackChild?.selected,
      unselected: child.unselected || fallbackChild?.unselected,
      route: child.route,
      appId: child.app_id || null,
      appName: child.app_name || null,
      externalUrl: child.external_url || null,
    };

    if (fallbackChild?.matchPatterns && !child.matchPatterns) {
      processedChild.matchPatterns = fallbackChild.matchPatterns;
    } else if (child.matchPatterns) {
      processedChild.matchPatterns = child.matchPatterns;
    }

    if (fallbackChild?.sub_sections && !processedChild.sub_sections) {
      processedChild.sub_sections = fallbackChild.sub_sections;
    }

    return processedChild;
  };

  let processedDynamicSections = dynamicSidebarSections.value.map((section) => {
    const fallbackSection = findFallbackSection(section);
    const selectedIcon = getIcon(section, fallbackSection, "selected");
    const unselectedIcon = getIcon(section, fallbackSection, "unselected");

    let processedSections = [];

    if (section.sections && section.sections.length > 0) {
      processedSections = section.sections
        .filter((child) => !DESKTOP_ONLY_APP_CODES.includes(child.id))
        .map((child) => processChildSection(child, fallbackSection))
        .filter(Boolean);

      if (section.id === "crm") {
        const apiSlugSet = new Set(
          dynamicCRMSections.value.map((obj) => obj.slug),
        );
        const defaultSlugSet = new Set(standardObjects.map((o) => o.slug));
        const allowedSet = new Set(allowedStandardObjects.value);

        processedSections = processedSections.filter((child) => {
          if (child.id === entityTypes.TASKS) {
            return false;
          }

          // Filter standard objects based on allowed_objects API
          if (defaultSlugSet.has(child.id) && !allowedSet.has(child.id)) {
            return false;
          }

          // Filter customers based on allowed_objects API
          if (child.id === "customers" && !allowedSet.has("customers")) {
            return false;
          }

          // Filter lists based on allowed_objects API
          if (child.id === "lists" && !allowedSet.has("lists")) {
            return false;
          }

          if (isDynamicCRMEnabled()) {
            if (child.id === "customers" && apiSlugSet.has("customers")) {
              return false;
            }

            return !(
              isStandardObjectsV2Enabled() &&
              defaultSlugSet.has(child.id) &&
              apiSlugSet.has(child.id)
            );
          }

          return true;
        });
      }
    }

    return {
      id: section.id,
      text: section.text,
      permission:
        section.permission ||
        fallbackSection?.permission ||
        permissionConstants.ALL,
      selected: selectedIcon,
      unselected: unselectedIcon,
      route: section.route,
      alignment: section.alignment || sidebarOptionPosition.TOP,
      sections: processedSections,
    };
  });

  if (isDynamicCRMEnabled() && dynamicCRMSections.value?.length > 0) {
    const crmSection = processedDynamicSections.find(
      (section) => section.id === "crm",
    );
    const crmAppId = crmSection?.sections?.find((s) => s.appId)?.appId || null;

    processedDynamicSections = getSideBarOptionsWithDynamicCRM(
      dynamicCRMSections.value,
      processedDynamicSections,
      crmAppId,
      "CRM",
    );
  }

  return [assistantItem, ...processedDynamicSections, appStoreItem];
});

const routeToFeatureKey = {
  "/linkedin/logs": "linkedin_blocks",
  "/customer_success/alerts": "customer_success_alerts",
};

const checkSinglePermission = (permission) => {
  if (!permission || permission.toLowerCase() === permissionConstants.ALL)
    return true;
  const perm = permission.toLowerCase();
  if (store.getters["rbac/isABACMode"]) {
    return (
      store.getters["rbac/canPerformAction"](perm, "ENABLED") ||
      store.getters["rbac/canPerformAction"](perm, "VIEW")
    );
  }
  return store.getters["rbac/hasPermission"](perm);
};

const hasSectionPermission = (permission) => {
  if (Array.isArray(permission)) {
    if (permission.length === 0) return true;
    return permission.every((p) => checkSinglePermission(p));
  }
  return checkSinglePermission(permission);
};

const isSectionEnabled = (section) => {
  const id = section?.id;
  if (id && featureFlags[id] && localStorage.getItem(id) !== "true")
    return false;

  const route = section?.route;
  if (route && routeToFeatureKey[route]) {
    const key = routeToFeatureKey[route];
    if (localStorage.getItem(key) !== "true") return false;
  }

  return true;
};

const filteredSideBarOptions = computed(() => {
  let options = sideBarOptionsArr.value
    .filter(
      (option) =>
        isSectionEnabled(option) && hasSectionPermission(option.permission),
    )
    .map((option) => {
      if (!option.sections) return option;
      return {
        ...option,
        sections: option.sections.filter(
          (section) =>
            isSectionEnabled(section) &&
            hasSectionPermission(section.permission),
        ),
      };
    })
    .filter((option) => {
      if (option.sections && option.sections.length === 0) return false;
      return true;
    });

  if (!sidebarWrapperRef.value) return options;

  return options.filter((option) => {
    if (option.event === false) return false;
    if (
      option.event === true &&
      !sidebarWrapperRef.value.hasEventHandler(option.id)
    )
      return false;
    return true;
  });
});

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    eventBus.emit("main-sidebar-expanded");
  }
  try {
    localStorage.setItem(
      localStorageConstants.SIDEBAR_STATE_KEY,
      isExpanded.value,
    );
  } catch (error) {
    console.error("Failed to update sidebar state in localStorage: ", error);
  }
};

const handleSectionClick = (sectionId) => {
  activeSection.value = activeSection.value === sectionId ? null : sectionId;
};

const handleEventClick = (eventId) => {
  if (sidebarWrapperRef.value) {
    sidebarWrapperRef.value.handleSidebarEvent(eventId);
  }
};

const initializeSidebarStateFromStorage = () => {
  try {
    isExpanded.value =
      localStorage.getItem(localStorageConstants.SIDEBAR_STATE_KEY) !== "false";
  } catch (error) {
    console.error("Failed to read sidebar state from localStorage: ", error);
    isExpanded.value = true;
  }
};

const fetchAppStoreVersion = async () => {
  try {
    await store.dispatch("appStore/fetchAppStoreVersion");
  } catch (error) {
    console.error("Error fetching app store version:", error);
  }
};

const fetchFeatures = async () => {
  try {
    const { features } = await DashboardService.fetchEnabledFeatures();

    Object.entries(featureFlags).forEach(([key, flag]) => {
      features[flag]
        ? localStorage.setItem(key, "true")
        : localStorage.removeItem(key);
    });
  } catch (error) {
    console.error("Error fetching features:", error);
  }
};

const fetchDynamicSidebarSections = async () => {
  try {
    const response = await AppStoreService.getSidebarSections();
    if (response?.success && response?.sidebar) {
      dynamicSidebarSections.value = response.sidebar;
    }
  } catch (error) {
    console.error("Error fetching dynamic sidebar sections:", error);
  }
};

const refreshDynamicCRMObjects = async () => {
  if (isDynamicCRMEnabled()) {
    await fetchDynamicCRMListObjects();
  }
};

const refetchSidebarSections = async () => {
  await fetchDynamicSidebarSections();
};

let sidebarRefetchTimeout = null;
const scheduleSidebarRefetch = () => {
  if (sidebarRefetchTimeout) clearTimeout(sidebarRefetchTimeout);
  sidebarRefetchTimeout = setTimeout(() => {
    refetchSidebarSections();
    sidebarRefetchTimeout = null;
  }, 250);
};

watch(isExpanded, (newVal) => {
  if (!newVal) activeSection.value = null;
});

watch(
  () => route.path,
  (newPath) => {
    if (newPath.includes("/projects/chats")) {
      store.dispatch("chats/clearUnreadIndicator");
      store.commit("chats/SET_LAST_FETCHED_CHAT_ID", null);
    }
  },
);

const handleCollapseMainSidebar = () => {
  if (isExpanded.value) {
    isExpanded.value = false;
    localStorage.setItem(localStorageConstants.SIDEBAR_STATE_KEY, false);
  }
};

onMounted(async () => {
  if (isPublicRoute.value || !isUserAuthenticated.value) {
    return;
  }

  if (!localStorage.getItem("userPermissions")) {
    await fetchPermissions();
  }
  await fetchAppStoreVersion();
  await fetchFeatures();
  eventBus.on("collapse-main-sidebar", handleCollapseMainSidebar);
  await fetchDynamicSidebarSections();

  await fetchAllowedObjects();
  if (isDynamicCRMEnabled()) {
    await fetchDynamicCRMListObjects();
  }

  initializeSidebarStateFromStorage();

  eventBus.on("permissions-updated", fetchFeatures);
  eventBus.on("dynamic-crm-objects-updated", refreshDynamicCRMObjects);
  eventBus.on("app-installed", scheduleSidebarRefetch);
});

onUnmounted(() => {
  eventBus.off("permissions-updated", fetchFeatures);
  eventBus.off("dynamic-crm-objects-updated", refreshDynamicCRMObjects);
  eventBus.off("app-installed", scheduleSidebarRefetch);
  eventBus.off("collapse-main-sidebar", handleCollapseMainSidebar);
});

defineExpose({
  refetchSidebarSections,
});
</script>

<style>
.sidebar {
  padding: 2px 0 8px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  color: var(--foreground-rgb);
}

.sidebar_item {
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  gap: 8px;
  transition: background-color 0.2s ease;
  margin: 0 8px;
}

.sidebar_item_selected,
.sidebar_item:not(.disabled):hover {
  background: var(--sidebar-item-selected);
}

.sidebar_item:not(.cursor-not-allowed):hover {
  background-color: var(--sidebar-item-selected);
}
</style>
